import {
  clearPasswordRecoveryPending,
  hashIndicatesPasswordRecovery,
  isPasswordRecoveryPending,
  markPasswordRecoveryPending,
  passwordResetRedirectUrl,
} from "@/auth/authRedirect";
import { supabase } from "@/lib/supabase";
import type { AuthChangeEvent, Session, User } from "@supabase/supabase-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

function isPasswordRecoveryEvent(event: AuthChangeEvent): boolean {
  return event === "PASSWORD_RECOVERY";
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  recoveryMode: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (password: string) => Promise<{ error: string | null }>;
  clearRecoveryMode: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [recoveryMode, setRecoveryMode] = useState(
    () => hashIndicatesPasswordRecovery() || isPasswordRecoveryPending(),
  );

  const enterRecoveryMode = useCallback(() => {
    markPasswordRecoveryPending();
    setRecoveryMode(true);
  }, []);

  useEffect(() => {
    let mounted = true;

    if (hashIndicatesPasswordRecovery()) {
      enterRecoveryMode();
    }

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      if (isPasswordRecoveryEvent(event) || hashIndicatesPasswordRecovery()) {
        enterRecoveryMode();
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [enterRecoveryMode]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  }, []);

  const signOut = useCallback(async () => {
    clearPasswordRecoveryPending();
    setRecoveryMode(false);
    await supabase.auth.signOut();
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: passwordResetRedirectUrl(),
    });
    return { error: error?.message ?? null };
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (!error) {
      clearPasswordRecoveryPending();
      setRecoveryMode(false);
    }
    return { error: error?.message ?? null };
  }, []);

  const clearRecoveryMode = useCallback(() => {
    clearPasswordRecoveryPending();
    setRecoveryMode(false);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      recoveryMode,
      signIn,
      signOut,
      requestPasswordReset,
      updatePassword,
      clearRecoveryMode,
    }),
    [
      session,
      loading,
      recoveryMode,
      signIn,
      signOut,
      requestPasswordReset,
      updatePassword,
      clearRecoveryMode,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
